import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogTitle,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Paper,
} from '@mui/material';

import BusinessIcon from '@mui/icons-material/Business';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ApartmentIcon from '@mui/icons-material/Apartment';

import { useNotification } from 'config/Notification';

import { fetchReportCompanies as fetchReportCompaniesService } from "services/modules/vapt/report";
import { fetchDomainsPerfomance as fetchDomainsPerfomanceService,fetchCompanyVAPTDimensions as fetchCompanyDimensionsService} from "services/modules/vapt/data";
import { setCompanyData as setCompanyDataService} from "stores/modules/idp/authreducer";

const PartnerDashboardPage = () => {
  // Redux dispatch
  const dispatch = useDispatch();

  // React router
  const navigate = useNavigate();

  // Notification
  const {openNotification} = useNotification();

  // Redux selectors
  const authenticatedUserContainer = useSelector((state) => state.auth.user);
  const dimensionsContainer = useSelector((state) => state.vaptData.vaptDimensions);
  const companyDataContainer = useSelector((state) => state.vaptReport.reportCompanies);

  // State variables
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [chooseDialog,setChooseDialog]=useState(false);
  const [categories,setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);

  
  // useEffect hook sets the initial value of company dropdown to the company of user who logs in
  useEffect(() => {
    if(authenticatedUserContainer){
      const company = authenticatedUserContainer.company;
      setSelectedCompany(company);
    }
  },[authenticatedUserContainer]);

  
  // useEffect hook sets the table header values
  useEffect(() => {
    if(tableData && tableData.length> 0 && selectedCategory){
      let columns = [
        {"name":"domain", "label":"Company"},
        {"name":"subcategory","label":"SubCategory"},
        {"name":"overall", "label":"Overall"},
      ];
      const categorySpecificDimensions = dimensionsContainer?.find(cat=>cat._id === selectedCategory._id)?.Dimensions;
      const filteredDimensions = categorySpecificDimensions?.filter((dim) => !!dim.subDimensions);
      Object.keys(tableData[0]).forEach((key) => {
        const value = filteredDimensions.find((dimension)=>
          dimension.slug === key
        );
        if (value){
          columns.push({
            "name": key,
            "label": value.label
          });
        }        
      });
      setTableColumns(columns);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[tableData]);

  useEffect(() => {
    if (selectedCompany && selectedCompany.id !== null) {
      setCategories(null);
      setSelectedCategory(null);
      fetchCompanyDimensionsData(selectedCompany.id);
    }
  }, [selectedCompany]);
  
 
  // useEffect fetches domains data and displays in the table cells
  useEffect(() => {
    const fetchDomainsData = async() => {
      let arrayofdomains = [selectedCompany.domain];
      let supplierheatmap = false;
      let suppliers = {};
      // setHeatmap(false);
      let supplierdomains = [];
      if (selectedCompany.heatmap && selectedCompany.heatmap === "true"){
        supplierheatmap = true;
      }
      if (selectedCompany.customers && (selectedCompany.customers.length > 0)){
        for (const customer of selectedCompany.customers){
          arrayofdomains.push(customer.domain);
          if (supplierheatmap && customer.supplier_type){
            supplierdomains.push(customer.domain);
            suppliers[customer.domain] = customer.supplier_type;
          }
        }
      }

      // Get Domains performance based on ECS Score
      let params = {
        arrayOf_domain: arrayofdomains.join(","),
        category:selectedCategory._id
      };
      const domainsresponse = await fetchDomainsPerfomanceService({params});
      if (domainsresponse.status === 200){
        const domainsData = domainsresponse.data.map((score) => {
          const newScore = { ...score };

          Object.keys(newScore.scores).forEach((key) => {
            newScore[key] = newScore.scores[key];
          });
          delete newScore.scores;
          Object.keys(newScore).forEach((key) => {
            if (key !== "domain" && key!=="subcategory"){
              newScore[key] = getLevelByScore(newScore[key]);
            }
          });
          return newScore;
        });
        setTableData(domainsData);
      }
      else{
        openNotification("Server error for domain performance",'error');
      }

     
    };

    if (selectedCompany && selectedCategory !== null){
      fetchDomainsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedCompany,selectedCategory]);

  // This method returns level based on score
  function getLevelByScore(score) {
    if (score === 0) {
      return "A";
    }
    if (score > 0 && score < 4) {
      return "B";
    }

    if (score >= 4 && score < 7) {
      return "C";
    }

    if (score >= 7 && score < 9) {
      return "D";
    }

    if (score >= 9) {
      return "F";
    }
  };

  const fetchCompanyDimensionsData = async (company) => {
    const params={company:company}
    let response = await fetchCompanyDimensionsService({ params });
    setCategories(response);
  };

  useEffect(() => {
    if(categories && categories.length>0){
      setSelectedCategory(categories[0]);
    }
  },[categories]);

  // This method handles change in selected company
  const onInputChange = (event, value, reason) => {
    if (value) {
      let params = {
        page:'1',
        limit:20,
        sortBy:'-createdAt',
        search: value,
        partner: "null",
      };
      if (value !== authenticatedUserContainer.company.name){
        dispatch(fetchReportCompaniesService(params));
      }
    } 
  };
  
  // This method handles navigation  to overall-dashboard when clicked on a partner customer
  function viewDashboard(domain){
    let company = {};
    if (domain === selectedCompany.domain){
      company = {
        companyid : selectedCompany.id,
        companyname : selectedCompany.name,
        domain: selectedCompany.domain
      };
    }
    else{
      if(selectedCompany.customers){
        const customer = selectedCompany.customers.find((customer)=>
          customer.domain === domain
        );
        company = {
          companyid : customer.id,
          companyname : customer.name,
          domain: customer.domain
        };
      }
    }
    dispatch(setCompanyDataService(company));
    navigate(`/vapt/dashboard/over-all?category=${selectedCategory?.slug}`);
  }

  // This method checks whether the given company is a partner or not based on domain
  function checkPartner(domain){
    if (selectedCompany && (domain === selectedCompany.domain)){
      if (selectedCompany.subscriptionPlan &&
          selectedCompany.subscriptionPlan.slug.includes(
            'partner'
          )
        ){
          return true;
        }
    }
    return false
  }

  // This method checks whether the given company is a partner customer or not based on domain
  function checkPartnerCustomer(domain){
    if(selectedCompany && selectedCompany.customers){
      const customer = selectedCompany.customers.find((customer)=>
        customer.domain === domain
      );
      if (customer){
        return true
      }
    }
    return false
  }

  return (
    <div className='card-container'>
      <div className="main-container partner-dashboard-container">
        
        <div className='partner-dashboard-buttons'>
          <div className="row">
            <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4">
              <div className="row partner-dashboard-company">
                {(authenticatedUserContainer &&
                  authenticatedUserContainer.roles &&
                  authenticatedUserContainer.roles.some((role) =>
                    role.permissions.includes('GET_ALL_COMPANIES')
                  )
                  ) && (
                    <Autocomplete
                      options={companyDataContainer.results || []}
                      onInputChange={onInputChange}
                      getOptionLabel={(option) => option?.name || ''}
                      value={selectedCompany}
                      onChange={(e,value) => setSelectedCompany(value)}
                      renderInput={(params) => (
                        <TextField {...params}
                        label="Company"
                        variant="outlined"
                        size="small"
                          />
                      )}
                    />
                  )}
              </div>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4">
              <div className="row partner-dashboard-company">
                {(authenticatedUserContainer &&
                  authenticatedUserContainer.roles &&
                  authenticatedUserContainer.roles.some((role) =>
                    role.permissions.includes('GET_CHILD_COMPANIES')
                  )
                  ) && (
                    <Autocomplete
                     options={categories||[]}
                    getOptionLabel={(option) => option.label}
                    value={selectedCategory}
                    onChange={(event, value) =>{setSelectedCategory(value)}}
                    renderInput={(params) => (
                    <TextField {...params} label="Category" variant="outlined" size="small" />
                     )}
                     defaultValue={categories && categories.length > 0 ? categories[0] : null}
                   />
                  )}
              </div>
            </div>
            <div className="col-xs-12 col-sm-12 col-md-4 col-lg-4">
              <div className="partner-dashboard-actions">
                {(authenticatedUserContainer &&
                  authenticatedUserContainer.roles &&
                  authenticatedUserContainer.roles.some((role) =>
                    role.permissions.includes('CREATE_ANY_COMPANIES')
                  )
                  ) && (
                    <Button variant="contained" className="add-company font" onClick={() => setChooseDialog(true)}>Add company</Button>
                  )}
              </div>
            </div>
          </div>
        </div>
        <div className='partner-dashboard'>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {tableColumns.map((column) => (
                    <TableCell key={column.name}>{column.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, index) => (
                  <TableRow key={index}>
                    {tableColumns.map((column) => (
                      <TableCell key={column.name}>
                        {column.name === 'domain' ? (
                          <div className="sub_name_cont">
                            <div className="business-container">
                              <BusinessIcon className="business" />
                            </div>
                            <span className='text-bold partner-name-text' onClick={() => viewDashboard(row[column.name])} >{row[column.name]}</span>
                            <div className={checkPartner(row[column.name]) ? "partner-company-container" : "partner-company-container-none"}>
                              <LocalOfferIcon fontSize="small" className='partner' />
                            </div>
                            <div className={checkPartnerCustomer(row[column.name]) ? "partner-customer-container" : "partner-customer-container-none"} >
                              <ApartmentIcon fontSize="small" className="partner" />
                            </div>
                          </div>
                        ) : (
                          <div className='rating-value'>
                            <span className={row[column.name]}>{row[column.name]}</span>
                          </div>
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog
            open={chooseDialog}
            onClose={() => setChooseDialog(false)}
            aria-labelledby="delete-dialog-title"
          >
            <DialogTitle  align="center">
              Choose customer type
              <Divider className="divider" />
            </DialogTitle>
            <DialogActions className="m-3">
              <Button variant="contained" onClick={()=>navigate(`/add-partnercompany`,{state:{customertype:'Partner'}})} >
                Partner
              </Button>
              <Button variant="contained" onClick={()=>navigate(`/add-partnercompany`,{state:{customertype:'endcustomer'}})} >
                End customer
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      </div>
    </div>
  )
}

export default PartnerDashboardPage;
